import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { isChrome, isIOS } from 'react-device-detect';
import styled, { createGlobalStyle, css } from 'styled-components';
import { copy, useNonScalableViewport, useShare } from '../../utils';
import { CopyIcon, SmallShareIcon } from '../Icon';
export function ViewToolbarCopyUrlButton({ url }) {
    const [copiedAt, setCopiedAt] = useState();
    const [clearCopiedAtAC, setClearCopiedAtAC] = useState();
    useEffect(function abortClearing() {
        return () => {
            clearCopiedAtAC?.abort();
        };
    }, [clearCopiedAtAC]);
    return (_jsxs(ViewToolbarShareButton, { type: "button", onClick: () => {
            copy(url, {
                onDone: () => {
                    const ac = new AbortController();
                    setClearCopiedAtAC(ac);
                    setCopiedAt(Date.now());
                    const { signal } = ac;
                    setTimeout(() => {
                        if (!signal.aborted) {
                            setCopiedAt(undefined);
                        }
                    }, 2000);
                },
            });
        }, children: [_jsx(CopyIcon, {}), _jsx("div", { children: copiedAt ? _jsx(_Fragment, { children: "Copied!" }) : _jsx(_Fragment, { children: "Copy link" }) })] }));
}
export function ViewToolbarShareUrlButton({ url }) {
    const { canShare, openSystemShareDialog } = useShare();
    if (!canShare) {
        return null;
    }
    return (_jsxs(ViewToolbarShareButton, { type: "button", onClick: () => {
            openSystemShareDialog(url);
        }, children: [_jsx(SmallShareIcon, {}), _jsx("div", { children: "Share link" })] }));
}
const ViewToolbarShareButton = styled.button `
    align-items: center;
    appearance: none;
    background: rgba(0, 0, 0, 0.1);
    font-weight: 500;
    font-size: 0.875rem;
    border: 0;
    gap: 0.5rem;
    border-radius: 0.375rem;
    height: 2rem;
    display: flex;
    color: #ffffff;
    padding: 0 0.5rem;
`;
export const ViewToolbarShareButtons = styled.div `
    align-items: center;
    display: flex;
    gap: 0.375rem;

    & > * {
        pointer-events: all;
    }
`;
export const StageContainer = styled.div `
    --Stage_PaddingTop: 0px;
    --Stage_PaddingBottom: 0px;

    position: relative;
`;
export const ViewToolbarSegment = styled.div `
    align-items: center;
    display: grid;
    gap: 1rem;
    justify-content: space-between;

    @media (min-width: 48rem) {
        justify-content: center;
    }
`;
export const TopViewToolbar = styled.div `
    padding: 1rem 1.5rem;
    height: 2rem;
    display: flex;
`;
export const BottomViewToolbar = styled.div `
    padding: 1.5rem;
    min-height: 3.75rem;
`;
export const StatusContainer = styled.div `
    flex-grow: 1;
`;
export const ViewRoot = styled.div `
    display: grid;
    grid-template-rows: auto 1fr auto;
    width: 100vw;
    height: 100vh;
`;
export function MobileView({ children, onTouchEnd: onTouchEndProp, innerRef, ...props }) {
    useEffect(resetScroll, []);
    useNonScalableViewport();
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyle, {}), _jsx(MobileViewRoot, { ...props, ref: innerRef, onTouchEnd: (e) => {
                    onTouchEndProp?.(e);
                    resetScroll();
                }, "$useFullscreenHack": isIOS, children: _jsx(MobileViewContent, { children: children }) })] }));
}
function resetScroll() {
    if (!isIOS) {
        return;
    }
    window.scrollTo({
        left: 1000,
        top: 1000,
        /*
         * In iOS Chrome (WebKit), smooth scrolling on large scrollable areas (e.g., 10000×10000)
         * can cause memory issues and tab reloads. Prefer 'auto' behavior to avoid crashes.
         */
        behavior: isChrome ? 'auto' : 'smooth',
    });
}
const MobileViewContent = styled.div `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 1000;
`;
const MobileViewRoot = styled.div `
    --BlockedPermissions_BorderRadius: 0;
    --Stage_PaddingTop: 0rem;
    --Stage_PaddingBottom: 0rem;
    --Stage_PaddingLeft: 0rem;
    --Stage_PaddingRight: 0rem;

    position: relative;
    z-index: 1;
    width: 100vw;
    height: 100vh;

    ${({ $useFullscreenHack }) => $useFullscreenHack &&
    css `
            width: 10000px;
            height: 10000px;
        `}
`;
export const BlockedPermissions = styled.div `
    align-items: center;
    background-color: var(--BlockedPermissions_Background, rgba(0, 0, 0, 0.6));
    border-radius: var(--BlockedPermissions_BorderRadius, 1rem);
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: var(--Stage_InnerWidth, 100%);
    height: var(--Stage_InnerHeight, 100%);
    padding: 2rem;
    box-sizing: border-box;

    button {
        white-space: initial;
        height: auto;
        min-height: 3.75rem;
        padding-top: 1rem;
        padding-bottom: 1rem;
        line-height: 1.75rem;
        pointer-events: all;
    }
`;
const GlobalStyle = createGlobalStyle `
    :root {
        --Layout_Background: #000000;
        --BlockedPermissions_Background: none;
    }

    html,
    body {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
    }

    /* WebKit browsers (Safari, Chrome on iOS) */
    html::-webkit-scrollbar,
    body::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
`;
